import React, { useState } from "react";
import "./ClubLicenseSettingsCard.css";
import { LICENSE_TIERS, resolveFeatures } from "../../context/ClubLicenseContext";

export const ClubLicenseSettingsCard = ({
  tier = LICENSE_TIERS.BASE,
  addonHr = false,
  onChange,
  readOnly = false,
  monthlyEvalUsed = null,
}) => {
  const [activeTab, setActiveTab] = useState("coach");

  const currentTier = String(tier || "base").toLowerCase();
  const currentAddon = Boolean(addonHr);

  const features = resolveFeatures(currentTier, currentAddon);
  const unlimited = features.monthly_eval_quota === -1;
  const usedLabel =
    monthlyEvalUsed == null || !Number.isFinite(Number(monthlyEvalUsed))
      ? null
      : Number(monthlyEvalUsed);
  const quotaLabel = unlimited
    ? usedLabel != null
      ? `${usedLabel} eval sessions this month · Unlimited`
      : "Unlimited eval sessions/mo"
    : usedLabel != null
      ? `${usedLabel}/${features.monthly_eval_quota} eval sessions/mo`
      : `${features.monthly_eval_quota} eval sessions/mo`;

  const handleSelectTier = (newTier) => {
    if (readOnly) return;
    if (onChange) {
      onChange({ tier: newTier, addonHr: currentAddon });
    }
  };

  const handleToggleHr = () => {
    if (readOnly) return;
    if (onChange) {
      onChange({ tier: currentTier, addonHr: !currentAddon });
    }
  };

  const coachFeatureCatalog = [
    { key: "webapp_access", label: "SmartTT-Club Webapp", core: true },
    { key: "pingflo_ai", label: "Pingflo AI Agents (Pingflo, Pingwi, Pingpro, Pingmynd)", core: true },
    { key: "smart_profiling", label: "SMART Profiling & Action Plans", core: true },
    { key: "king_of_the_table", label: "King of the Table Rotation (KotT)", core: true },
    { key: "video_review", label: "Video Review & Playback", core: true },
    { key: "auto_eval", label: "Auto-Evaluation (Single Camera)", core: true },
    { key: "gdpr_media_consent", label: "GDPR Media Consent Ledger", core: true },
    { key: "remote_camera_rig", label: "Multi-Camera / Remote Phone Rig Capture" },
    { key: "pose_frame_angles", label: "Pose Kinematics: Knee & Elbow Angles Overlay" },
    { key: "stroke_isolation", label: "Isolate Strokes Visualization & Selection" },
    { key: "stroke_overlay_export", label: "Biomechanical Skeleton Overlay Clip Export" },
    { key: "technique_labeling", label: "Coach Labeling (Good/Bad) & Technique Comments" },
    { key: "progress_snapshots", label: "Timeline Snapshot Progress Cards" },
    { key: "slow_mo_reel_export", label: "Clean Slow-Mo Reel 60fps Export" },
    { key: "pose_stroke_count", label: "Pose Stroke Count Analytics" },
    { key: "max_rally_detection", label: "Max Rally Detection & Stats" },
    { key: "ball_placement_analytics", label: "Ball Placement Distribution & Heatmaps" },
    { key: "ball_error_tracking", label: "Net & Out Error Tracking" },
    { key: "service_analytics", label: "Service Heat Map & Error Tracking" },
    { key: "hr_biofeedback", label: "Polar / BLE Live Heart Rate Biofeedback" },
    { key: "hr_effort_metrics", label: "Session HR Zones & Effort Load Metrics" },
  ];

  const playerFeatureCatalog = [
    { key: "webapp_access", label: "SmartTT-Club Player Portal", core: true },
    { key: "pingflo_ai", label: "All Pingflo AI Assistants", core: true },
    { key: "smart_profiling", label: "Personalized SMART Goals", core: true },
    { key: "king_of_the_table", label: "Live KotT Arena & Score Tracker", core: true },
    { key: "video_review", label: "Personal Video Replays", core: true },
    { key: "auto_eval", label: "Automated Drill Evals", core: true },
    { key: "gdpr_media_consent", label: "Self & Parental Video Consent", core: true },
    { key: "pose_frame_angles", label: "Knee & Elbow Biomechanics View" },
    { key: "stroke_isolation", label: "Stroke Clip Isolation" },
    { key: "progress_snapshots", label: "Coach Feedback & Labeled Snapshots Timeline" },
    { key: "slow_mo_reel_export", label: "Social Slow-Mo Reel Download" },
    { key: "pose_stroke_count", label: "Stroke Volume Tracking" },
    { key: "max_rally_detection", label: "Personal Best Rally Milestones" },
    { key: "ball_placement_analytics", label: "Ball Placement & Shot Accuracy" },
    { key: "service_analytics", label: "Service Consistency & Placement Map" },
    { key: "mobile_app_120fps", label: "iOS / Android 120fps High-Speed Camera Recording" },
    { key: "hr_biofeedback", label: "Real-Time Heart Rate Effort Biofeedback" },
    { key: "hr_effort_metrics", label: "Cardiorespiratory Effort & Recovery Metrics" },
  ];

  const activeCatalog = activeTab === "coach" ? coachFeatureCatalog : playerFeatureCatalog;

  return (
    <div className="club-license-card">
      <div className="club-license-header">
        <div className="club-license-title-group">
          <h3 className="club-license-title">Pilot License & Feature Set</h3>
          <span className={`club-license-badge badge-${currentTier}`}>
            {currentTier.toUpperCase()}
          </span>
          {currentAddon && (
            <span className="club-license-badge badge-pro" style={{ background: "#fef3c7", color: "#b45309", borderColor: "#fde68a" }}>
              + HR SENSOR
            </span>
          )}
        </div>
        <div className="tier-quota-pill">
          Storage: {features.video_retention_days} Days / {quotaLabel}
        </div>
      </div>

      <p className="club-license-desc">
        Select the evaluation license tier for this club during the pilot. Each tier unlocks distinct biomechanical and analytics capabilities for coaches and players.
      </p>

      {/* Tier Selection Grid */}
      <div className="license-tier-grid">
        {/* BASE TIER */}
        <div
          className={`license-tier-option ${currentTier === "base" ? "active" : ""}`}
          onClick={() => handleSelectTier(LICENSE_TIERS.BASE)}
        >
          <div className="tier-top-row">
            <span className="tier-name">Base License</span>
            <span className="tier-quota-pill">30d Retention</span>
          </div>
          <div className="tier-summary">
            Core club management, all AI conversational agents, video review, and single-camera auto-eval.
          </div>
          <ul className="tier-feature-bullets">
            <li><span className="tier-bullet-check">✓</span> SmartTT-Club Webapp</li>
            <li><span className="tier-bullet-check">✓</span> Pingflo AI, Pingwi, Pingpro, Pingmynd</li>
            <li><span className="tier-bullet-check">✓</span> King of the Table (KotT)</li>
            <li><span className="tier-bullet-check">✓</span> Video Review & Auto-Eval</li>
            <li><span className="tier-bullet-check">✓</span> GDPR Media Consent Ledger</li>
          </ul>
        </div>

        {/* PLUS TIER */}
        <div
          className={`license-tier-option tier-plus ${currentTier === "plus" ? "active" : ""}`}
          onClick={() => handleSelectTier(LICENSE_TIERS.PLUS)}
        >
          <div className="tier-top-row">
            <span className="tier-name">Plus License</span>
            <span className="tier-quota-pill">60d Retention</span>
          </div>
          <div className="tier-summary">
            Adds pose kinematics (knee & elbow angles), stroke clip isolation, coach labeling, and progress snapshots.
          </div>
          <ul className="tier-feature-bullets">
            <li><span className="tier-bullet-check">✓</span> <strong>Everything in Base</strong></li>
            <li><span className="tier-bullet-check">✓</span> <span className="tier-bullet-highlight">Knee & Elbow Angles</span> on Video Frame</li>
            <li><span className="tier-bullet-check">✓</span> <span className="tier-bullet-highlight">Isolate Strokes</span> Visual Analysis</li>
            <li><span className="tier-bullet-check">✓</span> Biomechanical Overlay Clip Generation</li>
            <li><span className="tier-bullet-check">✓</span> Coach Technique Tagging & Comments</li>
            <li><span className="tier-bullet-check">✓</span> Labeled Progress Snapshots Timeline</li>
            <li><span className="tier-bullet-check">✓</span> Multi-Camera / Phone Rig Capture</li>
            <li><span className="tier-bullet-check">✓</span> Clean 60fps Slow-Mo Reel Export</li>
          </ul>
        </div>

        {/* PRO TIER */}
        <div
          className={`license-tier-option tier-pro ${currentTier === "pro" ? "active" : ""}`}
          onClick={() => handleSelectTier(LICENSE_TIERS.PRO)}
        >
          <div className="tier-top-row">
            <span className="tier-name">Pro License</span>
            <span className="tier-quota-pill">365d / Unlimited</span>
          </div>
          <div className="tier-summary">
            Adds ball detection, stroke placement, errors, service heat maps, and 120fps high-speed mobile app capture.
          </div>
          <ul className="tier-feature-bullets">
            <li><span className="tier-bullet-check">✓</span> <strong>Everything in Plus</strong></li>
            <li><span className="tier-bullet-check">✓</span> <span className="tier-bullet-highlight">Pose Stroke Count</span> Analytics</li>
            <li><span className="tier-bullet-check">✓</span> <span className="tier-bullet-highlight">Max Rally Detection</span> & Count</li>
            <li><span className="tier-bullet-check">✓</span> <span className="tier-bullet-highlight">Ball Placement</span> & Accuracy Maps</li>
            <li><span className="tier-bullet-check">✓</span> Net & Out Error Detection</li>
            <li><span className="tier-bullet-check">✓</span> Service Heat Map & Fault Analysis</li>
            <li><span className="tier-bullet-check">✓</span> iOS/Android App (120fps Recording)</li>
          </ul>
        </div>
      </div>

      {/* HR SENSOR ADD-ON ROW */}
      <div className="addon-card">
        <div className="addon-info">
          <span className="addon-icon">❤️</span>
          <div>
            <div className="addon-text-title">Heart Rate Sensor Add-on (Polar / BLE)</div>
            <div className="addon-text-desc">
              Unlocks real-time cardiac biofeedback, 5-zone exertion analysis, recovery metrics, and mobile Bluetooth sensor pairing.
            </div>
          </div>
        </div>
        <button
          type="button"
          className="switch-toggle-btn"
          onClick={handleToggleHr}
          disabled={readOnly}
          aria-label="Toggle Heart Rate Sensor Add-on"
        >
          <div className={`switch-track ${currentAddon ? "checked" : ""}`}>
            <div className="switch-thumb" />
          </div>
        </button>
      </div>

      {/* COACH & PLAYER CAPABILITY BREAKDOWN */}
      <div className="role-capabilities-tabs">
        <div className="role-tabs-header">
          <button
            type="button"
            className={`role-tab-btn ${activeTab === "coach" ? "active" : ""}`}
            onClick={() => setActiveTab("coach")}
          >
            Coach Capabilities ({activeCatalog.filter(c => features[c.key]).length}/{activeCatalog.length})
          </button>
          <button
            type="button"
            className={`role-tab-btn ${activeTab === "player" ? "active" : ""}`}
            onClick={() => setActiveTab("player")}
          >
            Player Capabilities ({playerFeatureCatalog.filter(c => features[c.key]).length}/{playerFeatureCatalog.length})
          </button>
        </div>

        <div className="features-chip-grid">
          {activeCatalog.map((item) => {
            const isEnabled = Boolean(features[item.key]);
            return (
              <span
                key={item.key}
                className={`feature-chip ${isEnabled ? "enabled" : "disabled"}`}
                title={isEnabled ? "Active in this license" : "Requires higher license tier or add-on"}
              >
                <span>{isEnabled ? "✓" : "✕"}</span>
                <span>{item.label}</span>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ClubLicenseSettingsCard;
